import { Card, Headline, NotificationBanner, Text } from '@velocity/ui'
import clsx from 'clsx'
import * as React from 'react'
import { useCallback, useMemo } from 'react'
import { Form } from 'react-final-form'

import { useContent } from '@ngb-frontend/shared/context'
import { ALTERNATIVE_DATES_COUNT } from '@ngb-frontend/shared/rules'
import {
  type DateFormValues,
  type StepFormProps,
  type TimeFieldProps,
  type DateFieldProps,
  type TimeSlotFieldProps,
} from '@ngb-frontend/shared/types'
import {
  DateTimeField,
  DRIVER_STEP_FORM_ID,
  StepFooterDisabledSync,
} from '@ngb-frontend/shared/ui'
import { setDateTime } from '@ngb-frontend/shared/utils'

import { useDateFormStyles } from './DateForm.styled'

import type { FormRenderProps } from 'react-final-form'

export type DateFormProps = StepFormProps<DateFormValues> & {
  minDate?: string
  disabledDates?: string[]
  hint?: string
  fixedTime?: string
  timeConfig?: TimeFieldProps | TimeSlotFieldProps
}

export const DateForm: React.FC<DateFormProps> = ({
  onSubmit,
  initialValues,
  minDate,
  disabledDates,
  timeConfig,
  hint,
  fixedTime,
}) => {
  const c = useContent()
  const classes = useDateFormStyles()

  const handleSubmit = React.useCallback(
    (values: DateFormValues) => {
      const { date, time: formTime, alternativeDates } = values
      const time = fixedTime || formTime
      const dateTime = setDateTime(date, fixedTime || time)

      onSubmit?.({
        date: dateTime.toISOString(),
        time,
        alternativeDates: alternativeDates?.filter((a) => a?.date),
      })
    },
    [fixedTime, onSubmit],
  )

  const countryDisabledDates = useMemo(
    () => disabledDates || [],
    [disabledDates],
  )

  const renderAlternativeDateFields = useCallback(
    (formProps: FormRenderProps<DateFormValues, Partial<DateFormValues>>) => {
      const unselectableDates = (
        Array.from({ length: ALTERNATIVE_DATES_COUNT }).map((_, i) => {
          return formProps.values.alternativeDates?.[i]?.date || ''
        }) || []
      ).concat(countryDisabledDates)

      // Append also main date to the list of blocked
      const mainDate = formProps.values.date
      if (mainDate) unselectableDates.push(formProps.values.date)

      const altDateSections = unselectableDates.reduce((acc, _, idx) => {
        acc[idx] = [...unselectableDates]
        // remove self from block list
        if (
          unselectableDates[idx] &&
          acc[idx]?.[idx] &&
          unselectableDates[idx] !== mainDate
        ) {
          // @ts-ignore - The inference of non-undef not working properly
          acc[idx][idx] = ''
        }

        return acc
      }, {} as NonNullable<Record<number, DateFieldProps['disabledDates']>>)

      return (
        <>
          {Array.from({ length: ALTERNATIVE_DATES_COUNT }).map((_, idx) => (
            <DateTimeField
              key={idx}
              dateProps={{
                disabledDates: (altDateSections?.[idx] || []).filter(Boolean),
                required: !!formProps.values.alternativeDates?.[idx]?.time,
                onChange: (date) =>
                  formProps.form.mutators.clearAlternativeTime(date, idx),
                minDate,
                name: `alternativeDates[${idx}].date`,
                id: `alternative-date-${idx}`,
                'data-e2e-component': `alternative-date-${idx}`,
                'aria-label': `alternative date ${idx}`,
              }}
              timeProps={{
                ...timeConfig,
                required: !!formProps.values.alternativeDates?.[idx]?.date,
                name: `alternativeDates[${idx}].time`,
                id: `alternative-time-${idx}`,
                'data-e2e-component': `alternative-time-${idx}`,
                'aria-label': `alternative time ${idx}`,
              }}
              showTimeField={!fixedTime}
              inline
            />
          ))}
        </>
      )
    },
    [countryDisabledDates, minDate, timeConfig, fixedTime],
  )

  return (
    <Form<DateFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      mutators={{
        // Alternative times come only from prefilled/existing values — never
        // auto-filled from the main time. Just clear the time when its date is
        // cleared so the row returns to a clean, valid (empty) state.
        clearAlternativeTime: ([date, idx], state, { changeValue }) => {
          if (
            state.lastFormState?.values.alternativeDates?.[idx].time &&
            !date
          ) {
            changeValue(state, `alternativeDates[${idx}].time`, () => undefined)
          }
        },
      }}
    >
      {(formProps) => (
        <form
          id={DRIVER_STEP_FORM_ID}
          className={classes.form}
          onSubmit={formProps.handleSubmit}
          data-e2e-component="date-form"
        >
          <Card
            className={clsx(classes.card, classes.preferredCard)}
            data-e2e-component="preferred-date-card"
          >
            <Card.Body>
              <Headline variant="200" className={classes.heading}>
                {c(
                  fixedTime
                    ? 'steps.date.preferredDate'
                    : 'steps.date.preferredDateTime',
                )}
              </Headline>
              <div className={clsx(classes.rows, classes.rowsWithHint)}>
                <DateTimeField
                  hint={hint}
                  showTimeField={!fixedTime}
                  dateProps={{
                    required: true,
                    minDate,
                    disabledDates: countryDisabledDates,
                  }}
                  timeProps={{
                    ...timeConfig,
                    name: 'time',
                    required: true,
                  }}
                  inline
                />
              </div>
            </Card.Body>
          </Card>
          <Card
            className={classes.card}
            data-e2e-component="alternative-dates-card"
          >
            <Card.Body>
              <Headline variant="200" className={classes.headingWithText}>
                {c('steps.date.addAlternativeDates')}
              </Headline>
              <Text className={classes.explanation}>
                {c('steps.date.multipleDates')}
              </Text>
              <div className={classes.rows}>
                {renderAlternativeDateFields(formProps)}
              </div>
            </Card.Body>
          </Card>
          <NotificationBanner
            kind="information"
            data-e2e-component="date-availability-info"
          >
            {c('steps.date.availabilityInfo')}
          </NotificationBanner>
          <StepFooterDisabledSync disabled={formProps.invalid} />
        </form>
      )}
    </Form>
  )
}
